import { Handle, Position, useNodeConnections } from '@xyflow/react'
import { isFired } from './useFiredFlash'
import { fnArgValue } from '../../lib/functionArgs.js'
import { nodeControlClass } from '../../lib/nodeControlClass.js'
import { functionColor, functionHeaderBg, functionTitleInk, hasFunctionColor } from './functionColor.js'
import { selectedBorder } from './selectedBorder.js'

const EXEC_STYLE = { background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }

const TYPE_COLOR = { bool: '#d06060', float: '#60a0d0', int: '#d0a060', string: '#c060e8' }

const pinStyle = (type) => ({
  background: TYPE_COLOR[type] ?? '#888',
  width: 10, height: 10, borderRadius: '50%',
  border: `2px solid ${(TYPE_COLOR[type] ?? '#888') + '66'}`,
})

const stop = e => e.stopPropagation()

const argInputStyle = {
  marginLeft: 'auto', width: 72, boxSizing: 'border-box',
  background: '#08031a', border: '1px solid #b080f055', color: '#e8dcff',
  borderRadius: 3, fontSize: 10, fontFamily: 'var(--m-mono)', padding: '2px 5px', outline: 'none',
}

// One parameter row on a Function Call node. Wired: the wire drives the value
// and the box disappears. Unwired: a typed box writes `data.args[name]`, which
// the runtime passes instead (EventRuntime → fnArgValue).
function FunctionArgRow({ nodeId, pin, data }) {
  const handleId = `in-${pin.name}`
  const wired = useNodeConnections({ id: nodeId, handleType: 'target', handleId }).length > 0
  const value = fnArgValue(data, pin)
  const setArg = v => data.onChange?.('args', { ...(data.args ?? {}), [pin.name]: v })

  let editor = null
  if (!wired) {
    const common = {
      'aria-label': `argument ${pin.name}`,
      onPointerDown: stop, onMouseDown: stop, onClick: stop, onKeyDown: stop, onWheel: stop,
    }
    if (pin.type === 'bool') {
      editor = <input className={nodeControlClass()} type="checkbox" checked={value} onChange={e => setArg(e.target.checked)}
        {...common} style={{ marginLeft: 'auto', accentColor: TYPE_COLOR.bool }} />
    } else if (pin.type === 'int' || pin.type === 'float') {
      // Keep the raw text while typing ("-", "1.") — coercion happens on read.
      const raw = data.args?.[pin.name]
      editor = <input className={nodeControlClass()} type="number" step={pin.type === 'int' ? 1 : 0.1}
        value={typeof raw === 'string' || typeof raw === 'number' ? raw : value}
        onChange={e => setArg(e.target.value)}
        {...common} style={argInputStyle} />
    } else {
      editor = <input className={nodeControlClass()} type="text" value={value} placeholder="value"
        onChange={e => setArg(e.target.value)}
        {...common} style={argInputStyle} />
    }
  }

  return (
    <div className="nodrag nowheel"
      style={{ padding: '3px 12px 3px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
      <Handle type="target" position={Position.Left} id={handleId} style={pinStyle(pin.type)} />
      <span style={{ color: '#5a3870', fontSize: 9 }}>{pin.name}</span>
      <span style={{ color: '#3a2050', fontSize: 8 }}>({pin.type})</span>
      {editor}
    </div>
  )
}

// ── Function Call ─────────────────────────────────────────────────────────────
// Placed in the main graph or inside another function to invoke a named function.
export function FunctionCallNode({ id, data, selected }) {
  const fnDef = data.functions?.[data.functionId]
  const fired = isFired(data)

  // The function's own colour (set in its editor): the title bar is that exact
  // colour, with dark or light text for contrast. Unset = the original purple.
  const picked = hasFunctionColor(fnDef)
  const HEADER_BG = functionHeaderBg(fnDef, selected)
  const ink = picked ? functionTitleInk(HEADER_BG) : null
  const ACCENT = picked ? (ink === 'dark' ? '#111111' : '#f2f2f2') : functionColor(fnDef)
  const BORDER = fired ? '#ffcc44' : selected ? selectedBorder('#2a1050') : '#2a1050'

  const inputs  = fnDef?.inputs  ?? []
  const outputs = fnDef?.outputs ?? []

  return (
    <div
      onDoubleClick={e => { e.stopPropagation(); data.onOpenFunction?.() }}
      style={{
        background: selected ? '#120828' : '#0c0520',
        border: `2px solid ${BORDER}`,
        borderRadius: 6,
        minWidth: 200,
        fontFamily: 'var(--m-font)',
        boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
        cursor: 'default',
      }}
    >
      {/* Header row with exec handles. data-title-ink lets nodeLook.css keep
          dark title text on a light picked colour. */}
      <div data-title-ink={ink ?? undefined} style={{
        background: HEADER_BG,
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
        position: 'relative',
        borderRadius: '4px 4px 0 0',
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC_STYLE} />
        <span style={{ color: ACCENT, fontSize: 14, fontStyle: 'italic', fontFamily: 'Georgia, serif', lineHeight: 1 }}>ƒ</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5, flex: 1 }}>
          {fnDef ? fnDef.name : (data.functionId ?? '(missing function)')}
        </span>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC_STYLE} />
      </div>

      {!fnDef && (
        <div style={{ padding: '5px 12px', color: '#f06060', fontSize: 9 }}>
          Function not found. Was it deleted?
        </div>
      )}

      {/* Input data pins (left side) — an unwired pin gets an inline literal box */}
      {inputs.map(pin => (
        <FunctionArgRow key={pin.name} nodeId={id} pin={pin} data={data} />
      ))}

      {/* Output data pins (right side) */}
      {outputs.map(pin => (
        <div key={pin.name} style={{ padding: '3px 12px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative', justifyContent: 'flex-end' }}>
          <span style={{ color: '#3a2050', fontSize: 8 }}>({pin.type})</span>
          <span style={{ color: '#5a3870', fontSize: 9 }}>{pin.name}</span>
          <Handle type="source" position={Position.Right} id={`out-${pin.name}`} style={pinStyle(pin.type)} />
        </div>
      ))}

      {(inputs.length > 0 || outputs.length > 0) && (
        <div style={{ height: 4 }} />
      )}

      <div style={{ padding: '2px 12px 5px', color: '#2a1840', fontSize: 9, fontStyle: 'italic' }}>
        double-click to edit
      </div>
    </div>
  )
}

// ── Function Entry ────────────────────────────────────────────────────────────
// Auto-created locked node at the start of every function's inner graph.
// data.fnDef is injected by FunctionEditor from the current function def.
export function FunctionEntryNode({ data, selected }) {
  const inputs = data.fnDef?.inputs ?? []

  return (
    <div style={{
      background: selected ? '#0d1a10' : '#08110a',
      border: `2px solid ${selected ? selectedBorder('#1a4028') : '#1a4028'}`,
      borderRadius: 6,
      minWidth: 160,
      fontFamily: 'var(--m-font)',
      cursor: 'default',
    }}>
      <div style={{
        background: selected ? '#102018' : '#0a1810',
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
        position: 'relative',
        borderRadius: '4px 4px 0 0',
      }}>
        <span style={{ color: '#40c070', fontSize: 9, letterSpacing: 0.8, fontWeight: 'bold' }}>
          FUNCTION ENTRY
        </span>
        <Handle type="source" position={Position.Right} id="execOut" style={EXEC_STYLE} />
      </div>

      {/* Output pins for each input parameter (caller passes values here) */}
      {inputs.map(pin => (
        <div key={pin.name} style={{ padding: '3px 12px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative', justifyContent: 'flex-end' }}>
          <span style={{ color: '#304a38', fontSize: 8 }}>({pin.type})</span>
          <span style={{ color: '#4a7058', fontSize: 9 }}>{pin.name}</span>
          <Handle type="source" position={Position.Right} id={`out-${pin.name}`} style={pinStyle(pin.type)} />
        </div>
      ))}

      {inputs.length === 0 && (
        <div style={{ padding: '3px 12px 6px', color: '#1a3020', fontSize: 9, fontStyle: 'italic' }}>
          no inputs
        </div>
      )}
    </div>
  )
}

// ── Function Return ───────────────────────────────────────────────────────────
// Ends execution of a function and returns output values to the caller.
// data.fnDef is injected by FunctionEditor from the current function def.
export function FunctionReturnNode({ data, selected }) {
  const fired   = isFired(data)
  const outputs = data.fnDef?.outputs ?? []

  return (
    <div style={{
      background: selected ? '#120828' : '#0c0520',
      border: `2px solid ${fired ? '#ffcc44' : selected ? selectedBorder('#2a1050') : '#2a1050'}`,
      borderRadius: 6,
      minWidth: 140,
      fontFamily: 'var(--m-font)',
      cursor: 'default',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? '#1a0a38' : '#120730',
        padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
        position: 'relative',
        borderRadius: '4px 4px 0 0',
      }}>
        <Handle type="target" position={Position.Left} id="execIn" style={EXEC_STYLE} />
        <span style={{ color: '#9060d0', fontSize: 9, letterSpacing: 0.8, fontWeight: 'bold' }}>
          RETURN
        </span>
      </div>

      {/* Input pins for each output parameter (values returned to caller) */}
      {outputs.map(pin => (
        <div key={pin.name} style={{ padding: '3px 12px 3px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
          <Handle type="target" position={Position.Left} id={`in-${pin.name}`} style={pinStyle(pin.type)} />
          <span style={{ color: '#5a3870', fontSize: 9 }}>{pin.name}</span>
          <span style={{ color: '#3a2050', fontSize: 8 }}>({pin.type})</span>
        </div>
      ))}

      {outputs.length === 0 && (
        <div style={{ padding: '3px 12px 6px', color: '#2a1840', fontSize: 9, fontStyle: 'italic' }}>
          no outputs
        </div>
      )}
    </div>
  )
}
